import React, { useRef, useState, useEffect } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Delete } from '../ProjectIcons';

const CustomFileField: React.FC<WidgetProps> = ({ onChange, value, uiSchema }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
  const [editFields, setEditFields] = useState<{ [key: string]: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const acceptRaw = uiSchema?.['ui:options']?.accept;
  const accept = Array.isArray(acceptRaw) ? acceptRaw.join(',') : '.xml,.keyx';

  // Initialize component with existing form data (only on mount)
  useEffect(() => {
    if (value && typeof value === 'string') {
      try {
        const parsedValue = JSON.parse(value);
        if (Array.isArray(parsedValue)) {
          setFileData(parsedValue);
        }
      } catch (error) {
        console.error('Failed to parse existing form data:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    if (files.length > 0) {
      handleUpload(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    if (selectedFiles.length === 0 && fileData.length === 0) {
      setFiles([]);
      setErrors([]);
      setFileData([]);
      onChange(null);
    } else {
      setFiles(selectedFiles);
      setErrors([]);
    }
    event.target.value = '';
  };

  const handleUpload = async (selectedFiles = files) => {
    setErrors([]);

    const newFileData: any[] = [];
    const newErrors: string[] = [];

    // Get existing collarIDs
    const loadedCollarIDs = new Set(fileData.map((f) => f.parsedData?.collarID));
    const batchCollarIDs = new Set<string>();

    // Get names of already loaded files
    const loadedFileNames = new Set(fileData.map((f) => f.name));

    // Filter out files that are already loaded
    const filesToProcess = selectedFiles.filter((file) => !loadedFileNames.has(file.name));
    const duplicateCollarIDs: string[] = [];

    if (filesToProcess.length === 0) {
      setErrors(["All selected files are already loaded."]);
      return;
    }

    const processFile = (file: File): Promise<void> => {
      return new Promise((resolve) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        const isXml = file.type === 'text/xml' || ext === 'xml' || ext === 'keyx';
        if (!isXml) {
          newErrors.push(`${file.name} is not an XML file.`);
          resolve();
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(content, "application/xml");

            // Check for parsing errors
            const parseError = xmlDoc.getElementsByTagName("parsererror");
            if (parseError.length > 0) {
              throw new Error("Invalid XML format");
            }

            // Extract required fields
            const collar = xmlDoc.getElementsByTagName("collar")[0];
            const collarID = collar?.getAttribute("ID");
            const collarType = collar?.getElementsByTagName("collarType")[0]?.textContent;
            const comIDElement = collar?.getElementsByTagName("comID")[0];
            const comType = comIDElement?.getAttribute("comType");
            const comID = comIDElement?.textContent;
            const key = collar?.getElementsByTagName("key")[0]?.textContent;

            // Validate extracted fields
            if (!collarID || !collarType || !comType || !comID || !key) {
              throw new Error("Missing required fields in XML");
            }

            const parsedData = {
              collarID,
              collarType,
              comType,
              comID,
              key,
            };

            if (loadedCollarIDs.has(collarID) || batchCollarIDs.has(collarID)) {
              duplicateCollarIDs.push(collarID);
            } else {
              batchCollarIDs.add(collarID);
              // Add valid file data
              newFileData.push({
                name: file.name,
                parsedData,
                uploadDate: new Date().toLocaleString(),
              });
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : JSON.stringify(error);
            newErrors.push(`${file.name} is not a valid XML: ${errorMessage}`);
          } finally {
            resolve();
          }
        };
        reader.readAsText(file);
      });
    };

    await Promise.all(filesToProcess.map((file) => processFile(file)));

    if (duplicateCollarIDs.length > 0) {
      newErrors.push(`Collars ${[...new Set(duplicateCollarIDs)].join(", ")} info is already loaded.`);
    }

    setErrors((prev) => [
      ...prev,
      ...newErrors.map((error) =>
        typeof error === "string" ? error : JSON.stringify(error)
      ),
    ]);

    if (newFileData.length === 0) {
      // No new files to add
      return;
    }

    const updated = [...fileData, ...newFileData];
    setFileData(updated);
    onChange(
      JSON.stringify(
        updated.map((file) => ({
          name: file.name,
          parsedData: file.parsedData,
          uploadDate: file.uploadDate,
        }))
      )
    );
  };

  const handleDelete = (index: number) => {
    const updatedFileData = [...fileData];
    updatedFileData.splice(index, 1);
    setFileData(updatedFileData);

    onChange(
      JSON.stringify(
        updatedFileData.map((file) => ({
          name: file.name,
          parsedData: file.parsedData,
          uploadDate: file.uploadDate,
        }))
      )
    );
  };

  const handleOpenFileDialog = () => {
    setFiles([]);
    setErrors([]);
    fileInputRef.current?.click();
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (index: number) => {
    setCurrentEditIndex(index);
    setEditFields({ ...fileData[index].parsedData });
    setIsModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (currentEditIndex !== null) {
      try {
        const updatedFileData = [...fileData];
        updatedFileData[currentEditIndex].parsedData = { ...editFields };
        setFileData(updatedFileData);
        setIsModalOpen(false);

        // Call onChange with updated data
        onChange(
          JSON.stringify(
            updatedFileData.map((file) => ({
              name: file.name,
              parsedData: file.parsedData,
              uploadDate: file.uploadDate,
            }))
          )
        );
      } catch (error) {
        alert("Invalid data. Please correct it before saving.");
      }
    }
  };

  return (
    <div className="my-4">
      <div className="mb-4">
        <button
          type="button"
          className="bg-light-green dark:bg-dark-green-900 rounded-sm text-dark-green dark:text-dark-green-100 font-semibold py-2 px-3 mr-4"
          onClick={handleOpenFileDialog}
        >
          Select Collar Registration Files
        </button>
        <input
          type="file"
          accept={accept}
          multiple
          onChange={handleFileSelect}
          className="hidden"
          ref={fileInputRef}
          id="file-input"
        />
      </div>

      {/* Display selected filenames */}
      {files.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-bold dark:text-white">Selected Files:</h4>
          <ul className="list-disc pl-5">
            {files.map((file, index) => (
              <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                {file.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-bold dark:text-white">Errors:</h4>
          <ul className="list-disc pl-5">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-gray-700 dark:text-orange-300">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {fileData.length > 0 && (
        <div className="overflow-x-auto rounded-md overflow-hidden border border-gray-lines dark:border-neutral-gray-600">
          <table className="table-auto h-auto w-full cursor-pointer text-off-black dark:text-white bg-very-light-gray dark:bg-gray-950">
            <thead className="px-3 pt-3 pb-2 font-bold uppercase text-sm bg-light-gray dark:bg-status-green text-left border-b border-gray-lines dark:border-neutral-gray-800">
              <tr>
                <th className="p-3 bg-light-gray dark:bg-neutral-gray-900 dark:text-white border-b border-gray-lines dark:border-neutral-gray-600">Filename</th>
                <th className="p-3 bg-light-gray dark:bg-neutral-gray-900 dark:text-white border-b border-gray-lines dark:border-neutral-gray-600">Collar ID</th>
                <th className="p-3 bg-light-gray dark:bg-neutral-gray-900 dark:text-white border-b border-gray-lines dark:border-neutral-gray-600">Upload Date</th>
                <th className="p-3 bg-light-gray dark:bg-neutral-gray-900 dark:text-white border-b border-gray-lines dark:border-neutral-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fileData.map((file, index) => (
                <tr key={index} className="border-t border-gray-lines dark:border-gray-700 bg-white dark:bg-neutral-gray-800 hover:bg-gray-100 dark:hover:bg-neutral-gray-800">
                  <td className="p-3">{file.name}</td>
                  <td className="p-3">{file.parsedData.collarID}</td>
                  <td className="p-3">{file.uploadDate}</td>
                  <td className="p-3">
                    <div className="flex items-center">
                      <button
                        className="bg-light-green dark:bg-dark-green-900 text-dark-green dark:text-dark-green-100 font-bold py-2 px-2 mr-2"
                        onClick={() => handleEdit(index)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-very-light-orange dark:bg-orange-700 text-orange dark:text-orange-300 py-1 px-1"
                        onClick={() => handleDelete(index)}
                      >
                        <Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <>
          {/* Semi-transparent overlay */}
          <div className="fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 transition-colors duration-300 z-40"></div>

          {/* Modal content - fully opaque */}
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="bg-white dark:bg-gray-800 w-1/2 p-6 rounded shadow-lg pointer-events-auto">
              <h4 className="text-xl mb-[8px] text-off-black dark:text-white text-[24px]">Edit Record</h4>
              {/* Render a text field for each property */}
              {["collarID", "collarType", "comType", "comID", "key"].map((field) => (
                <div key={field} className="mb-3">
                  <label className="block font-semibold mb-1 dark:text-white">{field}</label>
                  {field === "key" ? (
                    <textarea
                      className="w-full border border-gray-lines dark:border-neutral-gray-600 rounded p-2 dark:bg-neutral-gray-700 dark:text-white"
                      value={editFields[field] || ""}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      rows={4}
                    />
                  ) : (
                    <input
                      className="w-full border border-gray-lines dark:border-neutral-gray-600 rounded p-2 dark:bg-neutral-gray-700 dark:text-white"
                      value={editFields[field] || ""}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                    />
                  )}
                </div>
              ))}
              <div className="flex justify-end mt-4">
                <button
                  className="btn btn-secondary text-medium-gray dark:text-gray-300 font-bold mr-2 px-4"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded mr-3 bg-dark-green hover:bg-status-green dark:hover:bg-green-600 text-white font-bold"
                  onClick={handleSaveEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomFileField;